import { Component, computed, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConsultantsService } from 'src/app/services/consultants.service';
import { ConsultantOrganizationService } from 'src/app/services/consultant-organization.service';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { Role } from 'src/app/models/user/role.model';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { UsersService } from 'src/app/services/users.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { DatePipe } from '@angular/common';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { entityExistsValidator } from 'src/app/validators/entity-exists.validator';
import { IData, RowDetailsDataComponent } from 'src/app/modules/shared/components/row-detail-data/row-details-data.component';
import { baseColDef } from '../../utils';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { BtnCellRenderer } from '../../components/btn-cell-renderer/btn-cell-renderer.component';
import { TextTooltipRenderer } from '../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatPhone } from '../../utils/phone.util';
import { PhoneInputDirective } from '../../directives/phone-input.directive';
import { tenDigitPhoneValidator } from '../../validators/phone.validator';
import { MatIconModule } from '@angular/material/icon';
import { AgGridWrapperComponent } from '../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { RightSidebarContainerComponent } from '../../components/right-sidebar-container/right-sidebar-container.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { SearchSortComponent } from '../../components/search-sort/search-sort.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { ProviderOrganizationHierarchy } from 'src/app/models/consultant/provider-organization-hierarchy.model';
import { MatSelectModule } from '@angular/material/select';

@Component({
    selector: 'app-consultant-list',
    templateUrl: './consultant-list.component.html',
    styleUrls: ['./consultant-list.component.scss'],
    standalone: true,
    imports: [
        MatSidenavModule,
        RightSidebarContainerComponent,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        RowDetailsDataComponent,
        MatTabsModule,
        SearchSortComponent,
        RouterModule,
        MatIconModule,
        AgGridWrapperComponent,
        MatPaginatorModule,
        PhoneInputDirective,
        MatSelectModule,
    ]
})
export class ConsultantListComponent implements OnInit {
    public organizationId: any;
    @ViewChild('drawer') drawer: MatDrawer;

    isEditing = false;
    isLoading: boolean;
    id: number;

    form: FormGroup = new FormGroup({
        email: new FormControl('', {
            validators: [Validators.email, Validators.required],
            updateOn: 'blur',
        }),
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        phoneNumber: new FormControl('', {
            validators: [
                Validators.required,
                tenDigitPhoneValidator(),
            ],
            updateOn: 'blur'
        }),
        consultantOrganizationId: new FormControl('', [Validators.required]),
    });

    pageSettings: {
        defaultPageSize: number;
        pageSizeOptions: number[];
    } = {
            defaultPageSize: 20,
            pageSizeOptions: [20, 50, 100],
        };

    datePipe = new DatePipe('en-US');
    currentPage: number = 0;
    pageSize: number = this.pageSettings.defaultPageSize;

    subscriptions: Subscription[] = [];
    organization: any;
    descriptionRowData: IData[] = [{
        title: 'Description',
        description: '-'
    }];

    searchControl: FormControl = new FormControl('');
    currentSortField = {
        sortValue: 'id',
    };
    currentIsAscending = false;
    totalCount: number = 0;
    searchKey: string = '';
    providerOrganizations: ProviderOrganizationHierarchy[] = [];
    selectedProviderOrgIds: number[] = [];
    sortForm: FormGroup = new FormGroup({
        sortField: new FormControl({
            sortValue: this.currentSortField.sortValue,
        }),
        isAscending: new FormControl(this.currentIsAscending),
    });

    sortOptions = [
        { label: 'First Name', sortValue: 'firstName' },
        { label: 'Last Name', sortValue: 'lastName' },
        { label: 'Email', sortValue: 'email' },
    ];

    public columnDefs: ColDef[] = [
        {
            ...baseColDef,
            field: 'firstName',
            flex: 2,
            cellRenderer: TextTooltipRenderer,
        },
        { ...baseColDef, field: 'lastName', cellRenderer: TextTooltipRenderer, flex: 2 },
        { ...baseColDef, field: 'email', cellRenderer: TextTooltipRenderer, flex: 3 },
        {
            ...baseColDef,
            field: 'phoneNumber',
            cellRenderer: TextTooltipRenderer,
            flex: 2,
            valueFormatter: (params) => formatPhone(params.value),
        },
        {
            ...baseColDef,
            field: 'assignedOrganizations',
            headerName: 'Assigned Provider Organizations',
            cellRenderer: TextTooltipRenderer,
            flex: 3,
            valueGetter: (params) => {
                const orgs = params.data?.assignedOrganizations;
                if (!orgs || orgs.length === 0) return 'None';
                return orgs.map((org: any) => org.name).join(', ');
            }
        },
    ];

    public rowData!: any[];

    readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
    readonly isAdmin = computed(() => this.userRole() === Role.Admin);

    gridAPI: GridApi;

    constructor(
        private consultantService: ConsultantsService,
        private organizationService: ConsultantOrganizationService,
        private route: ActivatedRoute,
        private deleteDialog: DeleteConfirmationDialogService,
        private userService: UsersService,
        private router: Router,
        private providerOrganizationsService: ProviderOrganizationsService
    ) { }

    ngOnInit(): void {
        this.initOrganization();
        this.loadProviderOrganizations();
        const searchSubscription = this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                tap(() => {
                    this.currentPage = 0;
                    this.reload();
                }),
            )
            .subscribe();
        this.subscriptions.push(searchSubscription);
    }
    private loadProviderOrganizations() {
        const subscription = this.providerOrganizationsService
            .getHierarchy()
            .subscribe((response) => {
                if (!response.isError && response.data) {
                    this.providerOrganizations = response.data;
                }
            });
        this.subscriptions.push(subscription);
    }
    private getAllChildrenIds(parentId: number): number[] {
        const org = this.providerOrganizations.find(o => o.id === parentId);

        if (!org || !org.childIds || org.childIds.length === 0) {
            return [];
        }
        let allChildIds: number[] = [...org.childIds];

        for (const childId of org.childIds) {
            const grandchildren = this.getAllChildrenIds(childId);
            allChildIds = allChildIds.concat(grandchildren);
        }
        return allChildIds;
    }
onProviderOrgSelectionChange(selectedIds: number[]) {
    const addedIds = selectedIds.filter(id => !this.selectedProviderOrgIds.includes(id));    
    const removedIds = this.selectedProviderOrgIds.filter(id => !selectedIds.includes(id));
    let updatedSelection = [...selectedIds];

    for (const addedId of addedIds) {
        const childIds = this.getAllChildrenIds(addedId);
        updatedSelection = [...new Set([...updatedSelection, ...childIds])];
    }
    
    for (const removedId of removedIds) {
        const childIds = this.getAllChildrenIds(removedId);
        updatedSelection = updatedSelection.filter(id => !childIds.includes(id));
    }

    this.selectedProviderOrgIds = updatedSelection;
}
    getOrganizationsForDisplay(): { id: number; displayName: string; level: number }[] {
        const displayOrgs: { id: number; displayName: string; level: number }[] = [];
        const parents = this.providerOrganizations.filter(org => org.parentId === null);

        const addOrgWithChildren = (org: ProviderOrganizationHierarchy, level: number) => {

            const indent = '  '.repeat(level);
            displayOrgs.push({
                id: org.id,
                displayName: `${indent}${org.name}`,
                level: level
            });
            if (org.childIds && org.childIds.length > 0) {
                const children = this.providerOrganizations.filter(o => org.childIds.includes(o.id));
                children.forEach(child => {
                    addOrgWithChildren(child, level + 1);
                });
            }
        };
        parents.forEach(parent => {
            addOrgWithChildren(parent, 0);
        });

        return displayOrgs;
    }
    private initOrganization() {
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) {
        this.organizationId = routeId;
        this.getOrganization();
        this.reload();
    } else {
        const subscription = this.userService.organizationId$.subscribe(orgId => {
        if (orgId) {
            this.organizationId = orgId.toString();
            this.getOrganization();
            this.reload();
        }
        });
        this.subscriptions.push(subscription);
    }
    }

    onChangeAvailability(data: any) {
        this.deleteDialog
            .openDeleteConfirmation(
                `This consultant user will be ${data.isActive ? 'disabled' : 'enabled'}.`,
                data.isActive ? 'Disable confirmation' : 'Enable confirmation',
                data.isActive ? 'Disable' : 'Enable'
            )
            .afterClosed()
            .pipe(
                switchMap((dialogClosed) => {
                    if (dialogClosed)
                        return this.consultantService.changeUserAvailability(
                            data.id,
                            !data.isActive
                        );
                    else return of(false);
                })
            )
            .subscribe((deleteConfirmation) => {
                if (deleteConfirmation) {
                    this.reload();
                }
            });
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach((s) => s.unsubscribe());
    }

    private getOrganization() {
        this.organizationService.getById(this.organizationId).subscribe((data) => {
            if (!data.isError) {
                this.organization = data.data;
                const addressParts = [];
                if (this.organization.mailingStreetAddress) {
                    addressParts.push(this.organization.mailingStreetAddress);
                }
                if (this.organization.mailingStreetAddressLine2) {
                    addressParts.push(this.organization.mailingStreetAddressLine2);
                }
                if (this.organization.mailingCity) {
                    addressParts.push(this.organization.mailingCity);
                }
                if (this.organization.mailingStateName) {
                    addressParts.push(this.organization.mailingStateName);
                }
                if (this.organization.mailingZipCode) {
                    addressParts.push(this.organization.mailingZipCode);
                }

                const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '-';

                this.descriptionRowData = [
                    {
                        title: 'Organization Details',
                        description: `Email: ${this.organization.email || '-'}\nPhone Number: ${formatPhone(this.organization.phoneNumber) || '-'}\nAddress: ${fullAddress}`
                    },
                ]
            }
        });
    }

    openConsultantSidebar(data: any = null) {
        if (data) {
            this.id = data.id;
            this.form.controls['email'].setAsyncValidators([
                entityExistsValidator(this.consultantService, data.email),
            ]);

            const getSus = this.consultantService
                .getById(this.id.toString())
                .pipe(
                    switchMap((res) => {
                        if (!res.isError) {
                            this.form.reset({
                                email: res.data.email,
                                firstName: res.data.firstName,
                                lastName: res.data.lastName,
                                phoneNumber: res.data.phoneNumber,
                                consultantOrganizationId: res.data.consultantOrganizationId,
                            });

                            return this.consultantService.getAssignedOrganizations(this.id.toString());
                        }
                        return of(null);
                    })
                )
                .subscribe((assignedOrgsResponse) => {
                    if (assignedOrgsResponse && !assignedOrgsResponse.isError) {
                        this.selectedProviderOrgIds = assignedOrgsResponse.data.items.map((org: any) => org.id);
                    }
                });

            this.subscriptions.push(getSus);
            this.isEditing = true;
            this.form.controls['email'].disable();
        } else {
            this.form.controls['consultantOrganizationId'].setValue(this.organizationId);
            this.form.controls['email'].setAsyncValidators([
                entityExistsValidator(this.consultantService),
            ]);
            this.form.controls['email'].enable();
            this.selectedProviderOrgIds = [];
            this.isEditing = false;
        }

        this.form.updateValueAndValidity();
        this.drawer.toggle();
    }

    close() {
        this.form.reset();
        this.selectedProviderOrgIds = [];
        this.drawer.close();
    }

    onSubmit() {
        if (this.form.valid) {
            this.isLoading = true;
            const data = {
                ...this.form.value,
                providerOrganizationIds: this.selectedProviderOrgIds
            };

            if (this.isEditing) {
                const updateSubscription = this.consultantService
                    .update(this.id.toString(), data)
                    .subscribe((data) => {
                        this.reload();
                        this.isLoading = false;
                        this.close();
                    });
                this.subscriptions.push(updateSubscription);
            } else {
                const addSubscription = this.consultantService
                    .add(data)
                    .subscribe((data) => {
                        this.reload();
                        this.isLoading = false;
                        this.close();
                    });
                this.subscriptions.push(addSubscription);
            }
        }
    }

    private reload() {
        this.isLoading = true;

        if (this.gridAPI) {
            this.gridAPI.showLoadingOverlay();
        }

        this.searchKey = this.searchControl.value?.toLowerCase() || '';
        const subscription = this.consultantService
            .getByOrganizationId(this.organizationId, {
                organizationId: this.organizationId,
                sortColumn: this.currentSortField.sortValue,
                sortOrder: this.currentIsAscending ? 'asc' : 'desc',
                page: this.currentPage + 1,
                pageSize: this.pageSize,
                searchKey: this.searchKey
            }).pipe(
                switchMap((response: any) => {
                    if (!response.isError) {
                        return of(response.data);
                    } else {
                        return of([]);
                    }
                })
            )
            .subscribe((data) => {
                if (this.gridAPI) {
                    this.gridAPI.hideOverlay();
                }
                this.rowData = data.items;
                this.totalCount = data.totalCount;
                this.isLoading = false;
                this.form.reset();
                this.drawer.close();
            });
        this.subscriptions.push(subscription);
    }

    setSortField() {
        const { sortField, isAscending } = this.sortForm.value;
        this.currentIsAscending = isAscending;
        this.currentSortField = sortField;

        this.reload();
    }

    resetSortField() {
        this.sortForm.setValue({
            sortField: this.currentSortField,
            isAscending: this.currentIsAscending
        })
    }

    handlePageEvent(event: PageEvent) {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        this.reload();
    }

    onGridReady(params: GridReadyEvent) {
        this.gridAPI = params.api;
        this.reload();
        this.adjustColumnVisibility(params.api);
    }

    private adjustColumnVisibility(gridApi: any) {
        if (!this.isAdmin()) return;

        const actionCol = {
            field: 'action',
            minWidth: 80,
            maxWidth: 80,
            filter: false,
            sortable: false,
            cellRenderer: BtnCellRenderer,
            cellRendererParams: {
                onEdit: this.isAdmin() ? this.openConsultantSidebar.bind(this) : undefined,
                onChangeAvailability: this.isAdmin() ? this.onChangeAvailability.bind(this) : undefined,
            },
        };

        gridApi.setColumnDefs([...this.columnDefs, actionCol]);
    }
}